import React, { useContext, memo } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { Grid, FormControl } from '@mui/material';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import { TextField, IconButton, Button, Typography } from '@totalsoft/rocket-ui';
import DetailedTextField from 'components/common/inputs/DetailedTextField';
import DetailedSlider from 'components/common/inputs/DetailedSlider';
import { RightsContext } from 'providers/RightsContext';
import { ParamsContext } from 'providers/ParamsContext';
import ChangeFinancialProductModal from './financialProductChange/Modal';
import { GridActionsWrapper } from 'components/common/CommonStyles';
import WrappedLinearProgress from 'components/common/WrappedLinearProgress';

const ContractModificationFinancingStructureComponent = ({
  document,
  isDirty,
  validationInfo,
  t,
  maxResidualValuePercentLoading,
  saving,
  onSave,
  onPropertyChange,
  offerFinancialProductRuleReadOnlyFields,
  areGuaranteeRatesVisible,
  onFinancialProductChangeModalToggle,
  showFinancialProductChangeModal,
  documentId,
  siteId
}) => {
  const { financingStructureWrite, readOnlyFull, isImage } = useContext(RightsContext);
  const { cvsChPercent, cvsChAmount } = useContext(ParamsContext);

  const ChangeFinancialProductButton = ({ onFinancialProductChangeModalOpen }) => {
    return (
        <IconButton
          onClick={onFinancialProductChangeModalOpen}
          title={t('OfferEdit.FinancingStructure.ChangeFinancialProduct')}
          id={'offerFinancingStructureCard_financialProductChangeBtn'}
          color='primary'
          variant='text'
          size='tiny'
        >
          <FindReplaceIcon />
        </IconButton>
    );
  };

  ChangeFinancialProductButton.propTypes = {
    onFinancialProductChangeModalOpen: PropTypes.func.isRequired
  };

  return (
    <>
      {showFinancialProductChangeModal && (
        <ChangeFinancialProductModal
          onModalClose={onFinancialProductChangeModalToggle}
          documentId={documentId}
          siteId={siteId}
          contractTypeId={document.contractTypeId}
        />
      )}
      <form noValidate>
        <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
        <Grid container spacing={4} px={1}>
          <Grid item xs={12} sm={6}>
            <TextField
              id={'offerFinancingStructureCard_financialProductName'}
              disabled
              fullWidth
              value={document.financialProductName || ''}
              label={t('OfferEdit.FinancingStructure.FinancialProductName')}
              endAdornment={<ChangeFinancialProductButton onFinancialProductChangeModalOpen={onFinancialProductChangeModalToggle} />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id={'offerFinancingStructureCard_contractTypeName'}
              disabled
              fullWidth
              value={document.contractTypeName || ''}
              label={t('OfferEdit.FinancingStructure.ContractTypeName')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id={'offerFinancingStructureCard_currency'}
              disabled
              fullWidth
              value={document.currencyCode || ''}
              label={t('OfferEdit.FinancingStructure.Currency')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id={'offerFinancingStructureCard_totalAcquisitionPrice'}
              disabled
              fullWidth
              isNumeric
              value={document.totalAcquisitionPrice || ''}
              inputProps={{ decimalScale: cvsChAmount }}
              label={t('OfferEdit.FinancingStructure.TotalAcquisitionPrice')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DetailedTextField
              id={'offerFinancingStructureCard_interestPercent'}
              value={document.interestPercent || ''}
              defaultValue={document.defaultInterestPercent}
              auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
                data: document.defaultInterestPercent
              })}
              fullWidth
              helperText={validationInfo.interestPercent}
              error={Boolean(validationInfo.interestPercent)}
              onChange={onPropertyChange('interestPercent')}
              decimalScale={cvsChPercent}
              label={t('OfferEdit.FinancingStructure.InterestPercent')}
              disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.interestPercentReadOnly}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id={'offerFinancingStructureCard_interestType'}
              disabled
              fullWidth
              value={
                document.isFixed || document.interestTypeId === null
                  ? t('OfferEdit.FinancingStructure.Fixed')
                  : t('OfferEdit.FinancingStructure.Variable')
              }
              label={t('OfferEdit.FinancingStructure.InterestType')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DetailedSlider
              id={'offerFinancingStructureCard_advancePercent'}
              value={document.advancePercent || 0}
              defaultValue={document.defaultAdvancePercent}
              min={document.minAdvancePercent}
              max={document.maxAdvancePercent}
              step={1}
              fullWidth
              decimalScale={cvsChPercent}
              label={t('OfferEdit.FinancingStructure.AdvancePercent')}
              onChange={onPropertyChange('advancePercent')}
              auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
                data: document.defaultAdvancePercent
              })}
              error={Boolean(validationInfo.advancePercent)}
              helperText={validationInfo.advancePercent}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DetailedTextField
              id={'offerFinancingStructureCard_advance'}
              value={document.advance == null ? '' : document.advance}
              decimalScale={cvsChAmount}
              defaultValue={document.defaultAdvance}
              auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
                data: document.defaultAdvance
              })}
              fullWidth
              error={Boolean(validationInfo.advance)}
              helperText={validationInfo.advance}
              onChange={onPropertyChange('advance')}
              label={t('OfferEdit.FinancingStructure.Advance')}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DetailedSlider
              fullWidth
              id={'offerFinancingStructureCard_financingValuePercent'}
              value={document.financingValuePercent || 0}
              min={document.minAdvancePercent}
              max={document.maxAdvancePercent}
              step={1}
              decimalScale={cvsChPercent}
              label={t('OfferEdit.FinancingStructure.FinancingValuePercent')}
              onChange={onPropertyChange('financingValuePercent')}
              error={Boolean(validationInfo.financingValuePercent)}
              helperText={validationInfo.financingValuePercent}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id={'offerFinancingStructureCard_financingValue'}
              disabled
              inputProps={{ decimalScale: cvsChAmount }}
              fullWidth
              isNumeric
              value={document.financingValue || 0}
              label={t('OfferEdit.FinancingStructure.FinancingValue')}
            />
          </Grid>
          {!document.hideInfoForDealersByContractTypes && (
            <>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                {maxResidualValuePercentLoading ? (
                  <FormControl fullWidth>
                    <Typography sx={{ mb: 2 }}>{t('OfferEdit.FinancingStructure.ResidualValuePercent')}</Typography>
                    <WrappedLinearProgress />
                  </FormControl>
                ) : (
                  <DetailedSlider
                    id={'offerFinancingStructureCard_residualValuePercent'}
                    value={document.residualValuePercent || 0}
                    defaultValue={document.defaultResidualValuePercent}
                    min={document.minResidualValuePercent}
                    max={document.maxResidualValuePercent}
                    step={1}
                    fullWidth
                    decimalScale={cvsChPercent}
                    label={t('OfferEdit.FinancingStructure.ResidualValuePercent')}
                    onChange={onPropertyChange('residualValuePercent')}
                    auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
                      data: document.defaultResidualValuePercent
                    })}
                    error={Boolean(validationInfo.residualValuePercent)}
                    helperText={validationInfo.residualValuePercent}
                    disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.rezidualPercentReadOnly}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                {maxResidualValuePercentLoading ? (
                  <FormControl fullWidth>
                    <Typography sx={{ mb: 2 }}>{t('OfferEdit.FinancingStructure.ResidualValue')}</Typography>
                    <WrappedLinearProgress />
                  </FormControl>
                ) : (
                  <DetailedTextField
                    id={'offerFinancingStructureCard_residualValue'}
                    value={document.residualValue == null ? '' : document.residualValue}
                    decimalScale={cvsChAmount}
                    defaultValue={document.defaultResidualValue}
                    auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
                      data: document.defaultResidualValue
                    })}
                    fullWidth
                    error={Boolean(validationInfo.residualValue)}
                    helperText={validationInfo.residualValue}
                    onChange={onPropertyChange('residualValue')}
                    label={t('OfferEdit.FinancingStructure.ResidualValue')}
                    disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.rezidualAmountReadOnly}
                  />
                )}
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DetailedSlider
              id={'offerFinancingStructureCard_managementFeePercent'}
              value={document.managementFeePercent || 0}
              defaultValue={document.defaultManagementFeePercent}
              min={document.minManagementFeePercent}
              max={document.maxManagementFeePercent}
              step={1}
              decimalScale={cvsChPercent}
              label={t('OfferEdit.FinancingStructure.ManagementFeePercent')}
              onChange={onPropertyChange('managementFeePercent')}
              auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
                data: document.defaultManagementFeePercent
              })}
              fullWidth
              error={Boolean(validationInfo.managementFeePercent)}
              helperText={validationInfo.managementFeePercent}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              fullWidth
              isNumeric
              id={'offerFinancingStructureCard_managementFee'}
              value={document.managementFee == null ? '' : document.managementFee}
              inputProps={{ decimalScale: cvsChAmount }}
              error={validationInfo && validationInfo.managementFee ? true : false}
              helperText={validationInfo?.managementFee}
              onChange={onPropertyChange('managementFee')}
              label={t('OfferEdit.FinancingStructure.ManagementFee')}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DetailedSlider
              id={'offerFinancingStructureCard_durationMonths'}
              value={document.durationMonths || 0}
              defaultValue={document.defaultDuration}
              min={document.minDurationMonths}
              max={document.maxDurationMonths}
              step={1}
              fullWidth
              label={t('OfferEdit.FinancingStructure.DurationMonths')}
              onChange={onPropertyChange('durationMonths')}
              auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
                data: document.defaultDuration
              })}
              error={Boolean(validationInfo.durationMonths)}
              helperText={validationInfo.durationMonths}
              decimalScale={0}
              disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.durationReadOnly}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id={'offerFinancingStructureCard_paymentPlanName'}
              disabled
              fullWidth
              value={document.paymentPlanName || ''}
              label={t('OfferEdit.FinancingStructure.ScheduleType')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id={'offerFinancingStructureCard_monthlyPayment'}
              disabled
              fullWidth
              isNumeric
              value={document.monthlyPayment || ''}
              label={t('OfferEdit.FinancingStructure.MonthlyPayment')}
            />
          </Grid>
          {areGuaranteeRatesVisible && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                id={'offerFinancingStructureCard_numberOfGuaranteeRates'}
                disabled={readOnlyFull || document.numberOfGuaranteeRatesIsReadOnly || !financingStructureWrite}
                fullWidth
                isNumeric
                value={document.numberOfGuaranteeRates || ''}
                inputProps={{ decimalScale: 0 }}
                label={t('OfferEdit.FinancingStructure.NumberOfGuaranteeRates')}
                onChange={onPropertyChange('numberOfGuaranteeRates')}
                error={validationInfo.numberOfGuaranteeRates ? true : false}
                helperText={validationInfo.numberOfGuaranteeRates}
              />
            </Grid>
          )}
          {document.logisticTaxExists && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                id={'offerFinancingStructureCard_logisticTax'}
                disabled={readOnlyFull || document.logisticTax.isReadOnly}
                fullWidth
                isNumeric
                inputProps={{ decimalScale: cvsChAmount }}
                value={document.logisticTax.value || ''}
                onChange={onPropertyChange('logisticTax.value')}
                label={t('OfferEdit.FinancingStructure.LogisticTax')}
                helperText={validationInfo.logisticTax && validationInfo.logisticTax.value}
                error={validationInfo.logisticTax && !validationInfo.logisticTax.isValid}
              />
            </Grid>
          )}
          {!isImage && (
            <GridActionsWrapper item xs={12}>
              <Button
                id={'offerFinancingStructureCard_saveBtn'}
                color='primary'
                variant='contained'
                onClick={onSave}
                disabled={readOnlyFull || saving || !validationInfo.isValid || !financingStructureWrite || !isDirty}
              >
                {saving ? t('Saving') + '...' : t('Save')}
              </Button>
            </GridActionsWrapper>
          )}
        </Grid>
      </form>
    </>
  );
};

ContractModificationFinancingStructureComponent.propTypes = {
  document: PropTypes.object.isRequired,
  isDirty: PropTypes.bool.isRequired,
  validationInfo: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  maxResidualValuePercentLoading: PropTypes.bool.isRequired,
  offerFinancialProductRuleReadOnlyFields: PropTypes.object.isRequired,
  areGuaranteeRatesVisible: PropTypes.bool.isRequired,
  onFinancialProductChangeModalOpen: PropTypes.func.isRequired,
  showFinancialProductChangeModal: PropTypes.bool.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  onFinancialProductChangeModalToggle: PropTypes.func.isRequired
};

export default withTranslation()(memo(ContractModificationFinancingStructureComponent));
